/**
 * ProjectRepository — reads and writes the projects table. SQL only (rule R3).
 */

import type { SqliteClient } from '../adapters/sqlite-client';
import type { Project } from '../types';
import { toProject } from './row-mapping';

/** Counts come from the media table, so every read shares this projection. */
const SELECT = `
  SELECT p.*,
    (SELECT COUNT(*) FROM media m WHERE m.project_id = p.id) AS item_count,
    (SELECT COUNT(*) FROM media m WHERE m.project_id = p.id AND m.bucket_id IS NOT NULL) AS reviewed_count,
    (SELECT COUNT(*) FROM media m
        JOIN buckets b ON b.id = m.bucket_id
       WHERE m.project_id = p.id AND b.kind = 'keep') AS kept_count
  FROM projects p
`;

export class ProjectRepository {
  constructor(private readonly db: SqliteClient) {}

  list(): Project[] {
    return this.db
      .all(`${SELECT} ORDER BY COALESCE(p.last_opened_at, p.created_at) DESC, p.id DESC`)
      .map(toProject);
  }

  byId(id: number): Project | null {
    const row = this.db.get(`${SELECT} WHERE p.id = ?`, [id]);
    return row ? toProject(row) : null;
  }

  create(name: string, previewTargetPx: number, now: number): number {
    return this.db.run(
      `INSERT INTO projects (name, auto_advance, preview_target_px, created_at, updated_at, last_opened_at)
       VALUES (?, 1, ?, ?, ?, ?)`,
      [name, previewTargetPx, now, now, now],
    ).lastId;
  }

  rename(id: number, name: string, now: number): void {
    this.db.run('UPDATE projects SET name = ?, updated_at = ? WHERE id = ?', [name, now, id]);
  }

  setAutoAdvance(id: number, value: boolean, now: number): void {
    this.db.run('UPDATE projects SET auto_advance = ?, updated_at = ? WHERE id = ?', [
      value ? 1 : 0,
      now,
      id,
    ]);
  }

  setLastViewed(id: number, mediaId: number | null): void {
    this.db.run('UPDATE projects SET last_viewed_media_id = ? WHERE id = ?', [mediaId, id]);
  }

  touchOpened(id: number, now: number): void {
    this.db.run('UPDATE projects SET last_opened_at = ? WHERE id = ?', [now, id]);
  }

  delete(id: number): void {
    this.db.run('DELETE FROM projects WHERE id = ?', [id]);
  }
}
